/**
 * Mapeo variable del API → custom property CSS (Infinity semántico DES-841 o puente --pwcs-*).
 * Los valores del back sobrescriben tokens de la librería en body;
 * pwcs-process-theme usa var(--pwcs-*, var(--action-*|--text-*)) con fallbacks Light/Dark.
 */

/** Tokens semánticos que Infinity consume directamente (DES-841 / NIF-751). */
export const CSS_CONFIG_INFINITY_VARS: Readonly<Record<string, string>> = {
	/** Color texto general (y token base para textos del flujo). */
	text_color: '--text-primary',
	/** NIF-751: título → token primary del DS. */
	text_color_title: '--text-primary',
	text_color_primary: '--text-strong',
	text_color_secondary: '--text-secondary',
	text_color_tertiary: '--text-tertiary',
	/** Color texto enlace (estado normal) — Infinity `--action-link` (NIF-751 Carmen 123499). */
	link_color: '--action-link',
	/** Fondo y borde base del botón primario (mismo token en diseño). */
	button_primary_background_color: '--action-primary',
	/** Fondo y borde hover del botón primario. */
	button_primary_background_color_hover: '--action-primary-hover',
	button_primary_border_color_hover: '--action-primary-border-hover',
	button_secondary_border_color_hover: '--action-secondary-border-hover',
	button_icon_border_round: '--radius-action-icon',
	button_icon_color: '--action-on-icon',
	button_icon_color_hover: '--action-on-icon-hover',
	button_icon_color_active: '--action-on-icon-active',
	button_icon_background_color: '--action-icon',
	button_icon_background_color_hover: '--action-icon-hover',
	button_icon_background_color_active: '--action-icon-active',
	/** Grafismos NIF-751 → tokens Infinity (+ bridges --pwcs-icon-* en PWCS_VARS). */
	icon_primary_color: '--icon-highlight',
	/** No mapear a `--surface-brand-secondary` (fondo tag-img-info DS; Carmen 123693). Solo bridge PWCS. */
	icon_secondary_background_color: '--surface-brand-tertiary',
	icon_secondary_color: '--icon-brand-tertiary',
	icon_border_color: '--surface-inverse',
	icon_background_color: '--surface-brand-primary',
	feedback_info_surface: '--feedback-info-surface',
	feedback_on_info_surface: '--feedback-on-info-surface',
	feedback_warning_surface: '--feedback-warning-surface',
	feedback_on_warning_surface: '--feedback-on-warning-surface',
	feedback_success_surface: '--feedback-success-surface',
	feedback_on_success_surface: '--feedback-on-success-surface',
	feedback_error_surface: '--feedback-error-surface',
	feedback_on_error_surface: '--feedback-on-error-surface',
	progress_border_round: '--radius-progress',
	capture_guide_color: '--feedback-info-solid',
	input_border_color: '--border-control',
	input_border_color_focus: '--border-control-active',
	/**
	 * Texto selección activa (Campos): borde selected + texto ítem select (Infinity `--text-highlight`).
	 */
	input_selection_color: '--border-control-selected',
	toggle_background_color_active: '--surface-selected',
	toggle_background_color_active_disabled: '--surface-selected-disabled',
	input_border_round: '--radius-control',
	border_round_secondary: '--radius-container-sm',
	tag_border_round: '--radius-tag',
	checkbox_border_round: '--radius-checkbox',
};

/**
 * Fallbacks CSS en `pwcs-process-theme` cuando `css_config` no trae `*_round`
 * (p. ej. `css_config: []`). Alineados a defaults del form de diseño en identity-admin
 * (`button_border_round: 4`, `border_round: 16` / Infinity `$br-xs` / `$br-md`).
 * Sin fallback, `var(--pwcs-radius-*)` queda invalid → `border-radius` initial 0 (NIF-226).
 */
export const CSS_CONFIG_RADIUS_FALLBACKS_PX = {
	button_border_round: 4,
	border_round: 16,
} as const;

/** Variables puente aplicadas en styles (pwcs-process-theme). */
export const CSS_CONFIG_PWCS_VARS: Readonly<Record<string, string>> = {
	text_typography: '--pwcs-font-family',
	/** Anti-`.dark`: Infinity redefine --text-* en tec-pwcs-layout.dark; estos puentes no. */
	text_color_title: '--pwcs-text-title',
	text_color_primary: '--pwcs-text-strong',
	text_color_secondary: '--pwcs-text-secondary',
	text_color_tertiary: '--pwcs-text-tertiary',
	border_round: '--pwcs-radius-general',
	button_border_round: '--pwcs-radius-button',
	/** Color enlace en reposo (puente; fallback final en tema a --action-link). */
	link_color: '--pwcs-link-color',
	/** Subrayado de enlaces: underline | none (vía text-decoration). */
	links_underline: '--pwcs-link-text-decoration',
	/** Subrayado en hover (NIF-751). */
	links_underline_hover: '--pwcs-link-text-decoration-hover',
	/** Trazo / contorno de iconos (tec-icon, SVG del stepper, etc.). */
	icon_border_color: '--pwcs-icon-border',
	/** Relleno interior del documento en ilustraciones SVG del stepper. */
	icon_background_color: '--pwcs-icon-background',
	/** Icono / acento principal en SVG compuestos (escrituras, etc.). */
	icon_primary_color: '--pwcs-icon-primary',
	/** Borde secundario: óvalo Lottie + trazo discontinuo docs-tutorial. */
	icon_secondary_border_color: '--pwcs-icon-secondary-border',
	icon_secondary_background_color: '--pwcs-icon-secondary-background',
	icon_secondary_color: '--pwcs-icon-secondary',
	progress_fill_color: '--pwcs-progress-fill',
	lottie_frame_border_color: '--pwcs-lottie-frame-border',
	lottie_frame_icon_border_color: '--pwcs-lottie-frame-icon-border',
	lottie_frame_background_color: '--pwcs-lottie-frame-bg',
	tag_icon_color: '--pwcs-tag-icon-color',
	tag_icon_background_color: '--pwcs-tag-icon-bg',
	/** Hover de enlace (puente; fallback en tema: --action-link-hover). */
	link_color_hover: '--pwcs-link-hover',
	button_primary_text_color: '--pwcs-primary-btn-text',
	button_primary_text_color_hover: '--pwcs-primary-btn-text-hover',
	button_primary_background_color: '--pwcs-primary-btn-bg',
	button_primary_background_color_hover: '--pwcs-primary-btn-bg-hover',
	button_primary_border_color: '--pwcs-primary-btn-border',
	button_primary_border_color_hover: '--pwcs-primary-btn-border-hover',
	button_secondary_text_color: '--pwcs-secondary-btn-text',
	button_secondary_text_color_hover: '--pwcs-secondary-btn-text-hover',
	button_secondary_border_color: '--pwcs-secondary-btn-border',
	button_secondary_border_color_hover: '--pwcs-secondary-btn-border-hover',
	button_secondary_background_color: '--pwcs-secondary-btn-bg',
	button_secondary_background_color_hover: '--pwcs-secondary-btn-bg-hover',
	/** Botón icono terciario (anti-`.dark`; mismo patrón que primary/secondary). */
	button_icon_color: '--pwcs-icon-btn-color',
	button_icon_color_hover: '--pwcs-icon-btn-color-hover',
	button_icon_color_active: '--pwcs-icon-btn-color-active',
	button_icon_background_color: '--pwcs-icon-btn-bg',
	button_icon_background_color_hover: '--pwcs-icon-btn-bg-hover',
	button_icon_background_color_active: '--pwcs-icon-btn-bg-active',
	/** Feedback / captura / campos: puentes que `.dark` de Infinity no redefine. */
	feedback_info_surface: '--pwcs-feedback-info-surface',
	feedback_on_info_surface: '--pwcs-feedback-on-info-surface',
	feedback_warning_surface: '--pwcs-feedback-warning-surface',
	feedback_on_warning_surface: '--pwcs-feedback-on-warning-surface',
	feedback_success_surface: '--pwcs-feedback-success-surface',
	feedback_on_success_surface: '--pwcs-feedback-on-success-surface',
	feedback_error_surface: '--pwcs-feedback-error-surface',
	feedback_on_error_surface: '--pwcs-feedback-on-error-surface',
	capture_guide_color: '--pwcs-capture-guide',
	input_border_color: '--pwcs-border-control',
	input_border_color_focus: '--pwcs-border-control-active',
	input_selection_color: '--pwcs-border-control-selected',
	toggle_background_color_active: '--pwcs-surface-selected',
	toggle_background_color_active_disabled: '--pwcs-surface-selected-disabled',
};

/**
 * Segundo target semántico para claves que ya tienen puente --pwcs-*.
 * Permite que componentes Infinity lean el override sin depender del tema PWCS.
 */
export const CSS_CONFIG_SEMANTIC_BRIDGE: Readonly<Record<string, string | readonly string[]>> = {
	link_color_hover: '--action-link-hover',
	link_hover: '--action-link-hover',
	button_primary_text_color: '--action-on-primary',
	/** Borde primary en reposo → token Infinity (además de --pwcs-primary-btn-border). */
	button_primary_border_color: '--action-primary-border',
	// border/text hover solo en --pwcs-*; no pisar --action-primary / --action-on-primary.
	// Infinity: --action-secondary = fondo; texto = --action-on-secondary*; borde = --action-secondary-border*.
	button_secondary_text_color: '--action-on-secondary',
	button_secondary_text_color_hover: '--action-on-secondary-hover',
	button_secondary_border_color: '--action-secondary-border',
	button_secondary_background_color: '--action-secondary',
	button_secondary_background_color_hover: '--action-secondary-hover',
	progress_fill_color: '--brand-primary',
	/**
	 * Dual-target solo `border_round` → `--radius-container`.
	 * `button_border_round` NO puentea `--radius-action`: Infinity lo usa también en
	 * `tec-button-collapse`; el radio de botones va solo por `--pwcs-radius-button`.
	 */
	border_round: '--radius-container',
	/** NIF-751: icon/brand-primary hereda el mismo hex que icon/highlight (secundario marca). */
	icon_primary_color: '--icon-brand-primary',
	/** Campos: mismo hex que border-control-selected para el ítem activo del select. */
	input_selection_color: '--text-highlight',
};

/** Claves brand_* persistidas en css_configs (cascade Admin + huérfanas PWCS). */
export const BRAND_CSS_CONFIG_KEYS = [
	'brand_primary',
	'brand_secondary',
	'brand_accent',
	'brand_emphasis',
	'brand_neutral',
	'brand_tone_secondary',
	'brand_tone_tertiary',
] as const;

export type BrandCssConfigKey = (typeof BRAND_CSS_CONFIG_KEYS)[number];

/**
 * Tokens Infinity sin picker Admin: hex de brand_* → CSS var.
 * Light vs dark según listado Carmen (NIF-751 comentario 122260).
 */
export const BRAND_ORPHAN_SEMANTICS_LIGHT: Readonly<Partial<Record<BrandCssConfigKey, readonly string[]>>> = {
	brand_primary: [
		'--action-primary-inverse',
		'--action-primary-inverse-border',
		'--action-icon-border-active',
	],
	brand_secondary: ['--brand-secondary', '--text-brand'],
	brand_accent: [
		'--action-primary-inverse-hover',
		'--action-primary-inverse-border-hover',
		'--action-secondary-inverse-hover',
		'--action-secondary-inverse-border-hover',
		'--action-icon-inverse-active',
		'--action-icon-inverse-border-active',
		'--action-link-inverse-hover',
		'--border-selected',
		'--brand-auxiliary',
		'--icon-brand-secondary',
	],
	brand_tone_tertiary: ['--action-success-hover'],
};

export const BRAND_ORPHAN_SEMANTICS_DARK: Readonly<Partial<Record<BrandCssConfigKey, readonly string[]>>> = {
	brand_primary: [
		'--action-primary-inverse',
		'--action-primary-inverse-border',
		'--action-icon-border-active',
	],
	brand_accent: ['--border-selected', '--brand-auxiliary', '--icon-brand-secondary'],
	brand_tone_tertiary: ['--action-success-hover'],
};

export function resolveBrandOrphanVarNames(
	apiVariable: string,
	skin: 'light' | 'dark' = 'light',
): string[] {
	const key = apiVariable.trim() as BrandCssConfigKey;
	const map = skin === 'dark' ? BRAND_ORPHAN_SEMANTICS_DARK : BRAND_ORPHAN_SEMANTICS_LIGHT;
	return [...(map[key] ?? [])];
}

export function isBrandCssConfigKey(variable: string): boolean {
	return (BRAND_CSS_CONFIG_KEYS as readonly string[]).includes(variable.trim());
}

/** Alias de claves que llegan desde variantes antiguas del backend. */
export const CSS_CONFIG_PWCS_VAR_ALIASES: Readonly<Record<string, string>> = {
	button_primary_border_hover_color: '--pwcs-primary-btn-border-hover',
	button_primary_border_hover: '--pwcs-primary-btn-border-hover',
	button_secondary_border_hover_color: '--pwcs-secondary-btn-border-hover',
	button_secondary_border_hover: '--pwcs-secondary-btn-border-hover',
	/** Sin prefijo link_color_ (misma semántica que link_color_hover). */
	link_hover: '--pwcs-link-hover',
	/** Legacy feedback (espejo admin) → tokens canónicos NIF-751. */
	tag_icon_alert_background_color: '--feedback-warning-surface',
	tag_icon_alert_color: '--feedback-on-warning-surface',
	tag_icon_success_background_color: '--feedback-success-surface',
	tag_icon_success_color: '--feedback-on-success-surface',
	tag_icon_error_background_color: '--feedback-error-surface',
	tag_icon_error_color: '--feedback-on-error-surface',
};

export const CSS_CONFIG_TEXT_TYPOGRAPHY_KEY = 'text_typography';
export const CSS_CONFIG_TEXT_COLOR_KEY = 'text_color';

/** Paleta de texto granular (NIF-751). Si hay alguna, no sincronizar legacy text_color → --text-*. */
export const CSS_CONFIG_TEXT_COLOR_GRANULAR_KEYS: readonly string[] = [
	'text_color_title',
	'text_color_primary',
	'text_color_secondary',
	'text_color_tertiary',
];

/** Tokens semánticos adicionales aplicados junto a text_color (puente PWCS + Infinity). */
export const CSS_CONFIG_TEXT_COLOR_SEMANTIC_SYNC: readonly string[] = [
	'--text-primary',
	'--text-secondary',
];

export function resolveCssConfigVarNames(apiVariable: string): string[] {
	const key = apiVariable.trim();
	const names: string[] = [];

	const infinityVar = CSS_CONFIG_INFINITY_VARS[key];
	if (infinityVar) {
		names.push(infinityVar);
	}

	const pwcsVar = CSS_CONFIG_PWCS_VARS[key] ?? CSS_CONFIG_PWCS_VAR_ALIASES[key];
	if (pwcsVar && !names.includes(pwcsVar)) {
		names.push(pwcsVar);
	}

	const semanticBridge = CSS_CONFIG_SEMANTIC_BRIDGE[key];
	if (semanticBridge) {
		const bridges = Array.isArray(semanticBridge) ? semanticBridge : [semanticBridge];
		for (const bridgeVar of bridges) {
			if (!names.includes(bridgeVar)) {
				names.push(bridgeVar);
			}
		}
	}

	return names;
}

/** Primer target del resolver multi (compat tests / usos legacy). */
export function resolveCssConfigVarName(apiVariable: string): string | null {
	const names = resolveCssConfigVarNames(apiVariable);
	return names.length > 0 ? names[0] : null;
}
